import { NPCS, SAGA, SAGA_CHAPTERS, uniqueFor, type BetId, type GlyphId, type NpcId, type SagaScene } from '@somnhold/content';
import type { ChatLine } from '@somnhold/protocol';
import { groupLevel, hasUnique, isDecision, isDragonAct, memoryAt, rerunPays, sagaAct, sagaBlocked, sagaDecision, sagaOf, sagaRerun, sceneChoices, sceneLines, type CampaignCommand, type CampaignState, type SagaMemory, type SagaVote } from '@somnhold/sim';
import type * as THREE from 'three';
import { audio } from '../audio/sfx.ts';
import { portraitThumb } from '../ui/thumbs.ts';
import { reasonText, t } from '../i18n.ts';
import { clear, h } from '../ui/dom.ts';
import { ICONS } from '../ui/icons.ts';

import type { ChatChannel } from '../game/chat.ts';
import type { CampaignService } from './service.ts';
import { betsSection } from './bets-panel.ts';
import { sidesSection, type SideActions } from './sides-panel.ts';

/** The closing choices that give a chapter's whole glyph (the Turtle 4's forge, the Turtle 6's remedies), by text key. */
const SCENE_GLYPH: Record<string, GlyphId | undefined> = {
  'saga.deep.forge.c.take': SAGA_CHAPTERS.find((c) => c.id === 'deep')?.glyph as GlyphId | undefined,
  'saga.maelis.remedies.c.take': SAGA_CHAPTERS.find((c) => c.id === 'maelis')?.glyph as GlyphId | undefined,
};

/**
 * The saga of the Turtle (docs/QUESTS.md): where the group is in the chronicle, what comes next and the
 * way to play it; scenes open full screen, line by line, and end on a choice.
 */

export interface SagaActions {
  cmd: (c: CampaignCommand) => Promise<boolean>;
  /** Plays the next battle act alone (`ghosts`: the absent friends the dragon's fight brings as spectres). */
  play: (ghosts?: string[]) => void;
  /** Opens a live room for the next battle act (online only). */
  together: ((ghosts?: string[]) => void) | null;
  /** Opens the waiting scene (the panel steps aside meanwhile). */
  scene: () => void;
  /** Opens the decision the group is voting on. */
  vote: () => void;
  /** Plays a finished chapter again from its start, or goes on with the one under way (no chapter). */
  rerun: (chapter?: number) => void;
  /** Takes on one of Brann and Hilde's bets (alone). */
  bet: (id: BetId) => void;
  /** The sagas beside the Turtle's. */
  sides: SideActions;
  rerender: () => void;
}

const nameOf = (s: CampaignState, pid: string): string => s.players.find((p) => p.id === pid)?.displayName ?? '—';

/** Who gets a say: every hero still in the campaign. */
const voters = (s: CampaignState): CampaignState['players'] => s.players.filter((p) => !p.left);

/** Votes one side needs to close the vote before its day is over: more than half the group. */
const majority = (s: CampaignState): number => Math.floor(voters(s).length / 2) + 1;

/** A side's share of the group, as a bar with a notch where the majority closes it. */
const meter = (s: CampaignState, n: number): HTMLElement => {
  const of = Math.max(1, voters(s).length);
  return h(
    'span.vote-meter',
    { style: `--fill:${Math.round((n / of) * 100)}%;--need:${Math.round((majority(s) / of) * 100)}%` },
    h('i'),
  );
};

/** Who voted for a side, as small name chips (yours first, called «you»). */
const voterChips = (s: CampaignState, me: string, pids: string[]): HTMLElement =>
  h('span.vote-who', {}, ...[...pids].sort((a, b) => (a === me ? -1 : b === me ? 1 : 0)).map((pid) => h(`span.vote-chip${pid === me ? '.me' : ''}`, {}, pid === me ? t('saga.vote.you') : nameOf(s, pid))));

/** Who voted what: each side with its count, a bar of the group and the names. */
const tally = (s: CampaignState, me: string, v: { chapter: number; act: number; votes: [string, string][] }): HTMLElement => {
  const ch = SAGA_CHAPTERS[v.chapter];
  const sc = ch?.acts[v.act];
  if (!ch || sc?.kind !== 'scene') return h('div');
  return h(
    'ul.saga-tally',
    {},
    ...sc.choices.map((c) => {
      const who = v.votes.filter((x) => x[1] === c.id).map((x) => x[0]);
      return h(`li${who.length ? '.on' : ''}`, {}, h('b', {}, String(who.length)), h('span', {}, t(`saga.${ch.id}.${sc.id}.c.${c.id}`)), meter(s, who.length), who.length ? voterChips(s, me, who) : '');
    }),
  );
};

/** How many voted, and who is still missing. */
const turnout = (s: CampaignState, v: SagaVote, count = true): HTMLElement => {
  const all = voters(s);
  const missing = all.filter((p) => !v.votes.some((x) => x[0] === p.id)).map((p) => p.displayName);
  return h(
    'p.vote-turnout',
    {},
    count ? h('b', {}, t('saga.vote.count', { n: v.votes.length, of: all.length })) : '',
    missing.length ? h('span', {}, t('saga.vote.missing', { names: missing.join(', ') })) : '',
  );
};

/** When a vote closes: a majority (or everyone voting) closes it before, the end of its day at the latest. */
const closes = (svc: CampaignService, v: SagaVote): string => {
  const hours = Math.ceil((v.at + SAGA.voteMs - svc.now()) / 3_600_000);
  return hours > 0 ? t('saga.vote.closes', { h: hours, need: majority(svc.state) }) : t('saga.vote.closesNow');
};

/** What the cast thinks of the garrison, in words. */
const mood = (n: number): string => t(n >= 2 ? 'saga.mood.trust' : n === 1 ? 'saga.mood.warm' : n === 0 ? 'saga.mood.wary' : n === -1 ? 'saga.mood.cold' : 'saga.mood.hostile');

const resLine = (r: typeof SAGA.reward): HTMLElement =>
  h('span.saga-res', {}, ...(['wood', 'stone', 'crystal', 'dust'] as const).map((k) => h('span.cost-chip', {}, h('span', { html: ICONS[k] }), String(r[k]))));

export const sagaPanel = (svc: CampaignService, a: SagaActions): HTMLElement => {
  const s = svc.state;
  const sg = sagaOf(s);
  const ch = SAGA_CHAPTERS[sg.chapter];
  const wrap = h('div.saga', { 'data-test': 'saga' });
  const block = sagaBlocked(s, svc.day());
  if (!ch) {
    wrap.append(h('div.saga-done', {}, h('h3', {}, t('saga.overTitle')), h('p', {}, t('saga.blocked.over'))));
  } else {
    // The chapter, its acts as a track, the provisions left.
    const track = h(
      'ol.saga-track',
      {},
      ...ch.acts.map((act, i) =>
        h(
          `li.${i < sg.act ? 'done' : i === sg.act ? 'now' : 'next'}`,
          { title: act.kind === 'battle' ? t(`saga.${ch.id}.${act.id}.title`) : t('saga.scene') },
          h('span', { html: act.kind === 'battle' ? ICONS.sword : ICONS.scroll }),
        ),
      ),
    );
    const pips = h('span.saga-prov', { title: t('saga.provisionsHint') }, ...Array.from({ length: Math.max(sg.provisions, SAGA.provisions) }, (_, i) => h(`i${i < sg.provisions ? '.on' : ''}`)));
    wrap.append(
      h(
        'header.saga-head',
        {},
        h('span.label', {}, t('saga.chapter', { n: sg.chapter + 1, of: SAGA_CHAPTERS.length })),
        h('h3', {}, t(`saga.${ch.id}.title`)),
        h('div.saga-row', {}, track, h('span.saga-provwrap', {}, h('span.small', {}, t('saga.provisions')), pips)),
      ),
    );
    const now = h('div.saga-now');
    const act = sagaAct(s);
    if (block === 'level') now.append(h('p.saga-lock', {}, h('span', { html: ICONS.lock }), t('saga.blocked.level', { n: ch.minGroup ?? 1, g: groupLevel(s, s.clock) })));
    else if (block === 'era') now.append(h('p.saga-lock', {}, h('span', { html: ICONS.lock }), t('saga.blocked.era', { era: t(`era.${ch.minEra ?? 1}`) })));
    else if (block === 'resting') now.append(h('p.saga-lock', {}, h('span', { html: ICONS.hourglass }), t(`saga.${ch.id}.loss`), ' ', t('saga.blocked.resting')));
    else if (block === 'campaignLost') now.append(h('p.saga-lock', {}, t('saga.blocked.campaignLost')));
    else if (act?.kind === 'scene' && sg.vote) now.append(h('p.saga-lock', {}, h('span', { html: ICONS.hourglass }), t('saga.vote.waits')));
    else if (act?.kind === 'scene') {
      const who = act.lines.find((l) => l.who !== 'narrator')?.who;
      now.append(
        h('p', {}, who ? t('saga.waiting', { who: t(`npc.${who}`) }) : t('saga.waitingStory')),
        h('button.btn', { 'data-test': 'saga-scene', onclick: () => a.scene() }, t('saga.openScene')),
      );
    } else if (act?.kind === 'battle') {
      // The dragon of the Burn: what its tries left on it, and the absent friends who come as spectres.
      const dragon = isDragonAct(act) ? dragonCard(svc) : null;
      now.append(
        h('p.saga-goal', {}, h('b', {}, t(`saga.${ch.id}.${act.id}.title`)), h('span', {}, t(`saga.${ch.id}.${act.id}.goal`))),
        dragon?.el ?? '',
        h(
          'div.saga-go',
          {},
          h('button.btn', { 'data-test': 'saga-play', onclick: () => a.play(dragon?.ghosts()) }, t('saga.play')),
          a.together ? h('button.btn.ghost', { 'data-test': 'saga-together', onclick: () => a.together?.(dragon?.ghosts()) }, t('saga.together')) : '',
        ),
        h('p.small', {}, t('saga.losingCosts')),
      );
    }
    wrap.append(now);
    const v = sg.vote;
    const vch = v ? SAGA_CHAPTERS[v.chapter] : undefined;
    if (v && vch) {
      const mine = v.votes.find((x) => x[0] === svc.me);
      wrap.append(
        h(
          'div.saga-vote',
          { 'data-test': 'saga-vote' },
          h('span.label', {}, t('saga.vote.title')),
          h('p', {}, t('saga.vote.what', { who: nameOf(s, v.by), title: t(`saga.${vch.id}.title`) })),
          tally(s, svc.me, v),
          turnout(s, v),
          h('p.small', {}, closes(svc, v)),
          h('button.btn', { 'data-test': 'saga-vote-open', onclick: () => a.vote() }, mine ? t('saga.vote.change') : t('saga.vote.cast')),
        ),
      );
    }
    wrap.append(
      h(
        'div.saga-reward',
        {},
        h('span.small', {}, t('saga.rewardTitle')),
        resLine(ch.final ? SAGA.finalReward : SAGA.reward),
        prizeGlyph(svc, sg.chapter) ?? h('span.small', { 'data-test': 'saga-prize' }, prizeLine(svc, sg.chapter)),
      ),
    );
  }
  // The sagas beside the Turtle's, then Brann and Hilde's bets.
  const sides = sidesSection(svc, a.sides);
  if (sides) wrap.append(sides);
  // Brann and Hilde's bets, beside the story.
  const bets = betsSection(svc, a.bet);
  if (bets) wrap.append(bets);
  // The cast the group has met, and what they think of the garrison.
  const met = NPCS.filter((n) => sg.aff[n] !== undefined);
  if (met.length)
    wrap.append(
      h(
        'div.saga-cast',
        {},
        h('span.label', {}, t('saga.cast')),
        ...met.map((n) => h('div.saga-npc', {}, portrait(n), h('div', {}, h('b', {}, t(`npc.${n}`)), h('span.small', {}, `${t(`npc.${n}.role`)} · ${mood(sg.aff[n] ?? 0)}`)))),
      ),
    );
  // Finished chapters, to play again from the start: the story as it was, what this hero still has to gain.
  const me = s.players.find((p) => p.id === svc.me);
  const mine = sagaRerun(s, svc.me);
  const doneChapters = SAGA_CHAPTERS.slice(0, Math.min(sg.chapter, SAGA_CHAPTERS.length));
  if (me && doneChapters.length) {
    const gain = (i: number): string => {
      const c = SAGA_CHAPTERS[i] as (typeof SAGA_CHAPTERS)[number];
      const unique = c.unique && !hasUnique(me, c.unique) ? c.unique : null;
      // The Turtle 4 and 6: their whole glyph, once per hero (docs/UNICAS.md §1).
      const glyph = !unique && c.glyph && !(me.hero.sagaGlyphs ?? []).includes(i) ? c.glyph : null;
      const prize = unique ? t('saga.rerun.prizeUnique', { slot: t(`slot.${unique}`) }) : glyph ? t('saga.rerun.prizeGlyph', { glyph: t(`glyph.${glyph}`) }) : null;
      if (rerunPays(s, svc.me, i)) return prize ? t('saga.rerun.gainXpAnd', { prize }) : t('saga.rerun.gainXp');
      return prize ? t('saga.rerun.gainPrize', { prize }) : t('saga.rerun.gainStory');
    };
    const going = mine ? SAGA_CHAPTERS[mine.chapter] : undefined;
    wrap.append(
      h(
        'div.saga-replays',
        {},
        h('span.label', {}, t('saga.rerun.title')),
        h('p.small', {}, t('saga.rerun.hint')),
        mine && going
          ? h('button.btn', { 'data-test': 'saga-rerun-go', onclick: () => a.rerun() }, t('saga.rerun.goOn', { title: t(`saga.${going.id}.title`), n: mine.act + 1, of: going.acts.length }))
          : '',
        ...doneChapters.map((c, i) =>
          h(
            'button.saga-rerun',
            { 'data-test': 'saga-rerun', onclick: () => a.rerun(i) },
            h('b', {}, t('saga.chapter', { n: i + 1, of: SAGA_CHAPTERS.length }), ' · ', t(`saga.${c.id}.title`)),
            h('span.small', {}, gain(i)),
          ),
        ),
      ),
    );
  }
  return wrap;
};

/**
 * What the chapter being played gives everyone who plays an act, besides materials and XP (docs/UNICAS.md §1): its
 * unique piece, its whole glyph (the Turtle 4 and 6), or, for a chapter begun before the prizes changed, the piece it
 * paid then (`pays`).
 */
const prizeLine = (svc: CampaignService, chapter: number): string => {
  const sg = sagaOf(svc.state);
  const ch = SAGA_CHAPTERS[chapter];
  const was = sg.pays && sg.pays.chapter === chapter ? sg.pays.unique : null;
  const slot = was ?? ch?.unique ?? null;
  if (slot) return t('saga.rewardUnique', { slot: t(`slot.${slot}`) });
  if (ch?.glyph) return t('saga.rewardGlyph', { glyph: t(`glyph.${ch.glyph}`) });
  return t('saga.rewardXp');
};

/**
 * A whole glyph as a prize (the Turtle 4 and 6, docs/UNICAS.md §1): a medallion with its mark, rimmed in dragon red,
 * its name and who gets it. Shown on the chapter's card and on the closing scene's choice that gives it.
 */
export const glyphMedal = (glyph: GlyphId, test: string): HTMLElement =>
  h(
    'div.saga-medal',
    { 'data-test': test },
    h('span.saga-medal-ico', { html: ICONS[glyph] }),
    h('span.saga-medal-txt', {}, h('span.saga-medal-cap', {}, t('saga.prize.whole')), h('b', {}, t(`glyph.${glyph}`)), h('span.small', {}, t('saga.prize.glyphNote'))),
  );

/** The chapter's glyph medallion, when it gives one (and not a piece, for a chapter begun before the prizes changed). */
const prizeGlyph = (svc: CampaignService, chapter: number): HTMLElement | null => {
  const sg = sagaOf(svc.state);
  const ch = SAGA_CHAPTERS[chapter];
  if ((sg.pays && sg.pays.chapter === chapter && sg.pays.unique) || ch?.unique || !ch?.glyph) return null;
  return glyphMedal(ch.glyph as GlyphId, 'saga-prize');
};

/** Who says the hint after a lost try at the dragon (docs/UNICAS.md §4.5): Orla only to a group that knows her. */
const hintWho = (s: CampaignState, hint: NonNullable<NonNullable<CampaignState['saga']>['dragon']>['hint']): NpcId =>
  hint === 'fire' || hint === 'scales' ? 'hilde' : hint === 'eaten' ? 'odna' : hint === 'glass' && s.sides?.burn?.done?.[3] ? 'orla' : 'brann';

/** The spectres left out of the dragon's fight on this device (the rest of the group comes by default). */
const ghostsKey = (id: string): string => `somnhold.dragonGhosts.${id}`;
const leftOut = (id: string): string[] => {
  try {
    const v = JSON.parse(localStorage.getItem(ghostsKey(id)) ?? '[]') as unknown;
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
};

/**
 * The dragon's card (§4.7, §4.11, §4.12): its scars and who made them, the armour given anyway, the last lost try's
 * hint, and which absent friends come along as spectres (all of them unless left out; up to eight in all).
 */
const dragonCard = (svc: CampaignService): { el: HTMLElement; ghosts: () => string[] } => {
  const s = svc.state;
  const dg = sagaOf(s).dragon;
  const el = h('div.dragon-card', { 'data-test': 'dragon-card' });
  const scars = dg?.scars.length ?? 0;
  if (scars) {
    const names = [...new Set(dg?.scars.map((x) => nameOf(s, x.pid)))].join(', ');
    el.append(h('p.dragon-scars', { 'data-test': 'dragon-scars' }, h('span.scar-marks', {}, ...Array.from({ length: scars }, () => h('i'))), h('span', {}, t(`saga.burn.scars.${Math.min(2, scars)}`), ' ', h('span.small', {}, t('saga.burn.scarsBy', { names })))));
  }
  if (dg?.planB) el.append(h('p.small', { 'data-test': 'dragon-planb' }, t('saga.burn.planB')));
  if (dg?.hint) {
    const who = hintWho(s, dg.hint);
    el.append(h('div.dragon-hint', { 'data-test': 'dragon-hint' }, portrait(who), h('div', {}, h('span.label', {}, t('saga.burn.lastTry')), h('b', {}, t(`npc.${who}`)), h('p', {}, `«${t(`saga.burn.hint.${dg.hint}`)}»`))));
  }
  const others = s.players.filter((p) => p.id !== svc.me && !p.left).slice(0, 7);
  const out = new Set(leftOut(s.id));
  const chips = others.map((p) => {
    const b = h(`button.ghost-chip${out.has(p.id) ? '' : '.on'}`, { type: 'button', 'aria-pressed': String(!out.has(p.id)), 'data-test': 'dragon-ghost' }, h('span', { html: ICONS.ghost }), p.displayName);
    b.addEventListener('click', () => {
      if (out.has(p.id)) out.delete(p.id);
      else out.add(p.id);
      b.classList.toggle('on', !out.has(p.id));
      b.setAttribute('aria-pressed', String(!out.has(p.id)));
      count.textContent = t('saga.burn.ghosts.count', { n: others.filter((o) => !out.has(o.id)).length, of: others.length });
      try {
        localStorage.setItem(ghostsKey(s.id), JSON.stringify([...out]));
      } catch {
        /* fine */
      }
    });
    return b;
  });
  const count = h('span.small', {}, t('saga.burn.ghosts.count', { n: others.filter((o) => !out.has(o.id)).length, of: others.length }));
  el.append(
    h(
      'div.dragon-ghosts',
      {},
      h('div.row', {}, h('span.label', {}, t('saga.burn.ghosts.title')), others.length ? count : ''),
      others.length ? h('div.ghost-chips', {}, ...chips) : h('p.small', {}, t('saga.burn.ghosts.none')),
      others.length ? h('p.small', {}, t('saga.burn.ghosts.hint')) : '',
    ),
  );
  return { el, ghosts: () => others.filter((p) => !out.has(p.id)).map((p) => p.id) };
};

/** The renderer the portraits are drawn with (set by the bastion). */
let faces: THREE.WebGLRenderer | null = null;
export const setPortraitRenderer = (r: THREE.WebGLRenderer): void => {
  faces = r;
};

/** A cast member's face: their miniature, head and shoulders (a plain medallion if it can't be drawn). */
export const portrait = (n: NpcId | 'narrator'): HTMLElement => {
  const el = h(`span.npc-portrait.npc-${n}`, { 'aria-hidden': 'true' });
  if (n === 'narrator') return el;
  try {
    if (faces) {
      el.append(h('img', { src: portraitThumb(faces, n), alt: '' }));
      el.classList.add('drawn');
      return el;
    }
  } catch {
    /* no WebGL for thumbnails: the medallion */
  }
  el.textContent = t(`npc.${n}`).replace(/^Ser /, '').charAt(0);
  return el;
};

/**
 * Which scene: the one the saga waits on (`saga`), the decision up for the group's vote (`vote`), or
 * the act a hero is at in a finished chapter played again (`rerun`: told as it was, decisions already taken).
 */
export type SceneMode = 'saga' | 'vote' | 'rerun';

/** The keep's chat, small, inside a decision: what was said, and a line to say something. */
const sceneChat = (svc: CampaignService, ch: ChatChannel): HTMLElement => {
  const log = h('div.scene-chat-log', { 'aria-live': 'polite' });
  let shown = '';
  const paint = (): void => {
    const lines: ChatLine[] = ch.chat.slice(-6);
    const key = lines.map((l) => `${l.at}|${l.pid}`).join(',');
    if (key === shown) return;
    shown = key;
    clear(log);
    if (!lines.length) log.append(h('p.small', {}, t('saga.vote.chatEmpty')));
    for (const l of lines) log.append(h('p', {}, h('b', {}, nameOf(svc.state, l.pid)), ' ', l.text));
    log.scrollTop = log.scrollHeight;
  };
  const input = h('input', { type: 'text', maxlength: 200, placeholder: t('saga.vote.chatHint'), 'aria-label': t('saga.vote.chatHint') });
  const send = (): void => {
    const text = input.value.trim();
    if (!text) return;
    ch.say(text);
    input.value = '';
    window.setTimeout(paint, 700);
  };
  // Typing here is not the bastion's Enter (that opens its own chat).
  input.addEventListener('keydown', (e) => {
    e.stopPropagation();
    if (e.key === 'Enter') send();
  });
  const el = h('div.scene-chat', { 'data-test': 'scene-chat' }, h('span.label', {}, t('saga.vote.chat')), log, h('div.scene-chat-say', {}, input, h('button.btn.small', { onclick: send }, t('saga.vote.say'))));
  paint();
  // Lines come with the presence poll: look again every couple of seconds while the scene is up.
  const timer = window.setInterval(() => (el.isConnected ? paint() : window.clearInterval(timer)), 2000);
  return el;
};

/** A scene, full screen: who speaks and what they say, line by line, then the group's choice (or, played again, the one it took). */
export const openScene = (svc: CampaignService, mode: SceneMode, cmd: (c: CampaignCommand) => Promise<boolean>, after: () => void, chat: () => ChatChannel | null = () => null): void => {
  const s = svc.state;
  const sg = sagaOf(s);
  // Where the scene is, copied: the state it comes from moves on as the scene is played.
  const live = mode === 'saga' ? sg : mode === 'vote' ? sg.vote : sagaRerun(s, svc.me);
  if (!live) return;
  const at = { chapter: live.chapter, act: live.act };
  const ch = SAGA_CHAPTERS[at.chapter];
  const act = ch?.acts[at.act];
  if (!ch || act?.kind !== 'scene') return;
  const sc = act as SagaScene;
  const mem: SagaMemory = mode === 'rerun' ? memoryAt(s, at.chapter, at.act) : sg;
  const lines = sceneLines(s, sc, mem);
  const decision = isDecision(s, sc, mem);
  const key = (id: string): string => `saga.${ch.id}.${sc.id}.${id}`;
  let i = 0;
  const face = h('div.scene-face');
  const name = h('div.scene-name');
  const text = h('p.scene-text');
  const choices = h('div.scene-choices');
  const next = h('button.btn.ghost.scene-next', { 'data-test': 'scene-next', onclick: () => step() }, t('saga.next'));
  // Back a line, to read one again (the choices wait on the last).
  const prev = h('button.btn.ghost.scene-prev', { 'data-test': 'scene-prev', onclick: () => back() }, t('saga.prev'));
  const close = (): void => {
    if (!el.isConnected) return;
    stop();
    document.removeEventListener('keydown', esc);
    el.remove();
    after();
  };
  const esc = (e: KeyboardEvent): void => {
    if (e.key === 'Escape') close();
  };
  const el = h(
    'div.scene',
    { role: 'dialog', 'aria-modal': 'true', 'data-test': 'scene', onclick: (ev: Event) => { if (ev.target === el || ev.target === text) step(); } },
    h(
      'div.scene-card',
      {},
      h('span.scene-kicker', {}, mode === 'rerun' ? `${t('saga.rerun.kicker')} · ` : '', t('saga.chapter', { n: at.chapter + 1, of: SAGA_CHAPTERS.length }), ' · ', t(`saga.${ch.id}.title`)),
      h('div.scene-who', {}, face, name),
      text,
      choices,
      h('div.scene-nav', {}, prev, next),
    ),
    h('button.iconbtn.scene-close', { 'aria-label': t('saga.later'), onclick: close, html: ICONS.close }),
  );
  /** A side to take: its words, what it costs, what it does. */
  const option = (c: (typeof sc.choices)[number], tag: string, attrs: Record<string, EventListener | string>): HTMLElement => {
    const cost = c.cost ? Object.entries(c.cost).map(([k, v]) => h('span.cost-chip', {}, h('span', { html: ICONS[k as 'stone'] }), `−${v}`)) : [];
    return h(
      tag as 'button',
      attrs,
      h('b', {}, t(key(`c.${c.id}`)), ...cost),
      t(key(`c.${c.id}.desc`)) !== key(`c.${c.id}.desc`) ? h('span.small', {}, t(key(`c.${c.id}.desc`))) : '',
      // The closing choice that hands out the chapter's whole glyph shows it as the prize it is.
      SCENE_GLYPH[key(`c.${c.id}`)] ? glyphMedal(SCENE_GLYPH[key(`c.${c.id}`)] as GlyphId, 'scene-glyph') : '',
    );
  };
  /**
   * A decision for the group, from the first choice to the group's answer: the sides as cards (yours
   * marked, each with its votes and a bar of the group), who is missing, when it closes, the chat. It
   * follows the state while it is up (other votes come in, the vote closes) and never leaves anyone
   * stuck: a choice on a vote someone else opened first is a vote on it, a failure says why in place.
   */
  let stop = (): void => undefined;
  const deciding = (): void => {
    clear(choices);
    const board = h('div.decide', { 'data-test': 'scene-vote' });
    const c2 = chat();
    const talk = c2 ? sceneChat(svc, c2) : null;
    const foot = h('div.decide-foot');
    choices.append(board, ...(talk ? [talk] : []), foot);
    let busy = '';
    let error = '';
    let shown = '';
    const openVote = (): SagaVote | null => {
      const v = sagaOf(svc.state).vote;
      return v && v.chapter === at.chapter && v.act === at.act ? v : null;
    };
    /** Whether the story has gone past this scene (the choice is in, or the vote closed). */
    const past = (): boolean => {
      const sg2 = sagaOf(svc.state);
      return sg2.chapter > at.chapter || (sg2.chapter === at.chapter && sg2.act > at.act);
    };
    const pick = async (id: string): Promise<void> => {
      if (busy) return;
      audio.play('click');
      busy = id;
      error = '';
      paint();
      const r = await svc.command(openVote() ? { t: 'sagaVote', chapter: at.chapter, act: at.act, choice: id } : { t: 'sagaChoice', chapter: at.chapter, act: at.act, choice: id });
      busy = '';
      if (!r.ok) error = reasonText(r.reason) ?? t('reason.generic');
      paint();
    };
    const paint = (): void => {
      const v = openVote();
      const closed = !v && past();
      const d = closed ? sagaDecision(svc.state, at.chapter, at.act) : null;
      const key = JSON.stringify([v?.votes ?? null, closed, d?.choice ?? '', busy, error, v ? closes(svc, v) : '']);
      if (key === shown) return;
      shown = key;
      clear(board);
      clear(foot);
      const mine = v?.votes.find((x) => x[0] === svc.me)?.[1];
      board.append(
        h(
          'div.decide-head',
          {},
          h('span.decide-tag', { html: ICONS.scroll }, h('span', {}, closed ? t('saga.vote.closedTitle') : t('saga.vote.group'))),
          v ? h('span.decide-count', {}, t('saga.vote.count', { n: v.votes.length, of: voters(svc.state).length })) : '',
        ),
      );
      const list = h('div.decide-sides');
      for (const c of closed ? sc.choices.filter((x) => sceneChoices(svc.state, sc).includes(x) || x.id === d?.choice) : sceneChoices(svc.state, sc)) {
        const votes = (closed ? d?.votes ?? [] : v?.votes ?? []).filter((x) => x[1] === c.id).map((x) => x[0]);
        const won = closed && c.id === d?.choice;
        const card = closed
          ? option(c, `div.scene-choice.static${won ? '.chosen' : '.dim'}`, {})
          : option(c, `button.scene-choice${c.id === mine ? '.chosen' : ''}${busy === c.id ? '.busy' : ''}`, {
              'data-test': 'scene-choice',
              'aria-pressed': c.id === mine ? 'true' : 'false',
              ...(busy ? { disabled: 'true' } : {}),
              onclick: () => void (c.id === mine ? undefined : pick(c.id)),
            });
        if (c.id === mine && !closed) card.querySelector('b')?.append(h('span.vote-badge', {}, t('saga.vote.mine')));
        if (won) card.querySelector('b')?.append(h('span.vote-badge', {}, t('saga.vote.decided')));
        if (busy === c.id) card.querySelector('b')?.append(h('span.vote-badge.wait', {}, t('saga.vote.sending')));
        if (v || closed) card.append(h('span.vote-line', {}, meter(svc.state, votes.length), h('span.vote-n', {}, String(votes.length))), votes.length ? voterChips(svc.state, svc.me, votes) : '');
        list.append(card);
      }
      board.append(list);
      if (error) board.append(h('p.decide-error', { role: 'alert' }, error));
      if (closed) {
        foot.append(h('button.btn.scene-done', { 'data-test': 'scene-done', onclick: close }, t('saga.next')));
        return;
      }
      if (v) board.append(turnout(svc.state, v, false), h('p.decide-note', {}, mine ? t('saga.vote.yours') : t('saga.vote.pick'), ' ', closes(svc, v)));
      else board.append(h('p.decide-note.scene-hint', {}, t('saga.vote.opens')));
      foot.append(h('button.btn.ghost.scene-done', { 'data-test': 'scene-done', onclick: close }, v && mine ? t('saga.vote.done') : t('saga.later')));
    };
    paint();
    // Other votes come with the state: look at it often while the decision is up.
    const off = svc.subscribe(() => (el.isConnected ? paint() : off()));
    const pull = window.setInterval(() => (el.isConnected ? void svc.refresh?.() : window.clearInterval(pull)), 3000);
    stop = () => {
      off();
      window.clearInterval(pull);
    };
  };
  /** Played again: the decision the group took, marked, and who voted for it; nothing to choose. */
  const told = (): void => {
    const d = sagaDecision(svc.state, at.chapter, at.act);
    const nextAct = (): void => {
      void cmd({ t: 'sagaRerunNext', chapter: at.chapter, act: at.act }).then((ok) => ok && close());
    };
    if (!decision) {
      for (const c of sceneChoices(s, sc, mem)) choices.append(option(c, 'button.scene-choice', { 'data-test': 'scene-choice', onclick: () => { audio.play('click'); nextAct(); } }));
      return;
    }
    for (const c of sc.choices.filter((c) => sceneChoices(s, sc, mem).includes(c) || c.id === d?.choice)) {
      const el2 = option(c, `div.scene-choice.static${c.id === d?.choice ? '.chosen' : '.dim'}`, {});
      if (c.id === d?.choice) {
        const who = d.votes.filter((x) => x[1] === c.id).map((x) => nameOf(s, x[0]));
        el2.append(h('span.scene-picked', {}, who.length ? t('saga.rerun.chosenBy', { names: who.join(', ') }) : t('saga.rerun.chosen')));
      }
      choices.append(el2);
    }
    if (!d) choices.append(h('p.small', {}, sagaOf(s).vote?.chapter === at.chapter && sagaOf(s).vote?.act === at.act ? t('saga.rerun.stillVoting') : t('saga.rerun.decided')));
    choices.append(h('button.btn.scene-done', { 'data-test': 'scene-done', onclick: nextAct }, t('saga.next')));
  };
  const show = (): void => {
    const l = lines[i];
    if (!l) return;
    clear(face);
    face.append(portrait(l.who));
    name.textContent = l.who === 'narrator' ? '' : t(`npc.${l.who}`);
    el.classList.toggle('narrator', l.who === 'narrator');
    text.textContent = t(key(l.id));
    text.classList.remove('in');
    void text.offsetWidth;
    text.classList.add('in');
    const last = i === lines.length - 1;
    next.classList.toggle('hidden', last);
    prev.classList.toggle('hidden', i === 0);
    // Stepping back from a decision: its board stops following the state until it is shown again.
    stop();
    stop = () => undefined;
    clear(choices);
    if (!last) return;
    if (mode === 'rerun') return told();
    // A decision for a group (online) goes to the vote; a local campaign's companions just go along.
    const group = decision && svc.kind !== 'local' && voters(svc.state).length > 1;
    if (mode === 'vote' || group) return deciding();
    for (const c of sceneChoices(s, sc))
      choices.append(
        option(c, 'button.scene-choice', {
          'data-test': 'scene-choice',
          onclick: async () => {
            audio.play('click');
            await cmd({ t: 'sagaChoice', chapter: at.chapter, act: at.act, choice: c.id });
            close();
          },
        }),
      );
  };
  const step = (): void => {
    if (i < lines.length - 1) {
      i++;
      show();
    }
  };
  const back = (): void => {
    if (i > 0) {
      i--;
      show();
    }
  };
  document.body.append(el);
  document.addEventListener('keydown', esc);
  show();
};

/** The saga as it stood before a battle act: to tell what changed once it is over. */
export interface SagaBefore {
  chapter: number;
  act: number;
  provisions: number;
  items: number;
}

export const sagaBefore = (svc: CampaignService): SagaBefore => {
  const sg = sagaOf(svc.state);
  const me = svc.state.players.find((p) => p.id === svc.me);
  return { chapter: sg.chapter, act: sg.act, provisions: sg.provisions, items: me ? [me.hero.gear.weapon, me.hero.gear.armor, me.hero.gear.amulet, ...me.hero.bag].filter((x) => x?.unique).length : 0 };
};

/**
 * How a saga act ended: won (the story goes on, maybe a chapter done), lost (a provision, maybe the
 * chapter). `heading` replaces «act won» (a closing scene ends a chapter); `onBack` gets the card.
 */
export const sagaResult = (svc: CampaignService, before: SagaBefore, won: boolean, title: string, onBack: (el: HTMLElement) => void, heading?: string, rerun = false): HTMLElement => {
  const sg = sagaOf(svc.state);
  const me = svc.state.players.find((p) => p.id === svc.me);
  const uniques = me ? [me.hero.gear.weapon, me.hero.gear.armor, me.hero.gear.amulet, ...me.hero.bag].filter((x) => x?.unique) : [];
  const fresh = uniques.length > before.items ? uniques[uniques.length - 1] : null;
  const chapterDone = sg.chapter > before.chapter;
  // Lost at the dragon, the chapter starts again from it (docs/UNICAS.md §4.8): the act stays, provisions come back.
  const atDragon = isDragonAct(SAGA_CHAPTERS[before.chapter]?.acts[before.act]);
  const chapterLost = !won && sg.chapter === before.chapter && sg.restDay !== undefined && ((sg.act === 0 && before.act > 0) || (atDragon && sg.provisions > before.provisions));
  const ch = SAGA_CHAPTERS[before.chapter];
  const lines: (HTMLElement | string)[] = [];
  if (rerun) lines.push(h('p', {}, won ? t('saga.rerun.onward') : t('saga.rerun.lost')));
  else if (chapterDone && ch) lines.push(h('p', {}, t('saga.result.chapterDone', { title: t(`saga.${ch.id}.title`) })), resLine(ch.final ? SAGA.finalReward : SAGA.reward));
  else if (chapterLost && ch) lines.push(h('p', {}, t(`saga.${ch.id}.loss`)), h('p.small', {}, t('saga.blocked.resting')));
  else if (!won) lines.push(h('p', {}, t('saga.result.provisions', { n: sg.provisions })));
  else lines.push(h('p', {}, t('saga.result.onward')));
  // A try at the dragon lost: the line of whoever saw the worst of it (§4.5), and a scar if it left one.
  const dg = sg.dragon;
  if (!won && !rerun && atDragon && dg?.hint) {
    const who = hintWho(svc.state, dg.hint);
    lines.push(h('div.dragon-hint', { 'data-test': 'dragon-hint' }, portrait(who), h('div', {}, h('b', {}, t(`npc.${who}`)), h('p', {}, `«${t(`saga.burn.hint.${dg.hint}`)}»`))));
  }
  if (!rerun && atDragon && dg?.scars.length && dg.scars.some((x) => x.pid === svc.me && x.at >= svc.now() - 60_000)) lines.push(h('p.small', { 'data-test': 'dragon-scar-new' }, t(`saga.burn.scars.${Math.min(2, dg.scars.length)}`)));
  if (fresh?.unique) lines.push(h('div.saga-unique', { style: '--rune:#ff4f3a' }, h('b', {}, t(`unique.${fresh.unique}`)), h('span.small', {}, t(`unique.${fresh.unique}.desc`))));
  else if (chapterDone && ch?.unique && me) {
    // Kept for a level the hero hasn't reached: show the piece and when it comes.
    const kept = me.hero.pendingUniques?.find((x) => x.slot === ch.unique);
    if (kept) {
      const id = uniqueFor(me.hero.heroClass, kept.slot).id;
      lines.push(h('div.saga-unique.kept', { style: '--rune:#ff4f3a' }, h('b', {}, t(`unique.${id}`)), h('span.small', {}, t('saga.result.uniqueKept', { n: kept.level })), h('span.small', {}, t(`unique.${id}.desc`))));
    }
  }
  // The story waits right behind a won act: the way back says so.
  const rr = sagaRerun(svc.state, svc.me);
  const story = won && (rerun ? !!rr && SAGA_CHAPTERS[rr.chapter]?.acts[rr.act]?.kind === 'scene' : !chapterDone && sagaAct(svc.state)?.kind === 'scene');
  const el: HTMLElement = h(
    'div.modal-back.saga-end',
    { 'data-test': 'saga-result' },
    h(
      'div.modal',
      { role: 'dialog', 'aria-modal': 'true' },
      h('header', {}, h('h2', {}, heading ?? (won ? t('saga.result.won') : t('saga.result.lost'))), h('p', {}, title)),
      h('div.body', {}, ...lines),
      h('footer', {}, h('button.btn', { 'data-test': 'saga-back', onclick: () => onBack(el) }, heading ? t('saga.next') : story ? t('saga.result.continue') : t('saga.result.back'))),
    ),
  );
  return el;
};
